/* eslint-disable */
import { useRef, useState } from 'react';
import {
  DEFAULT_AUTO_DETECT_LANGUAGES,
  SPEECH_PHRASE_LIST,
  LANGUAGE_CODE_MAPPING,
  DEFAULT_SPEECH_CONFIG,
} from '../constants';
import { correctSpeechText } from '../utils/speechCorrection';

declare global {
  interface Window {
    SpeechSDK: any;
  }
}

const SpeechSDK = window.SpeechSDK;

export interface SpeechText {
  st: number;
  text: string;
  lang: string;
  recognizing: boolean;
}

export const useSpeechRecognition = (
  autoDetectSourceLanguage: string[] = [...DEFAULT_AUTO_DETECT_LANGUAGES],
  token: string,
  onRecognizing: (data: SpeechText) => void,
  onRecognized: (data: SpeechText) => void,
  onStop?: (data: SpeechText) => void
) => {
  const recognizerRef = useRef<any>(null);
  const [startState, setStartState] = useState(false);
  const intervalRef = useRef<any>(null);
  const [speechText, setSpeechText] = useState<SpeechText>({
    st: Date.now(),
    text: '',
    lang: '',
    recognizing: true,
  });

  const startInit = async (_token: string, _lang?: string[]) => {
    setSpeechText({
      st: Date.now(),
      text: '',
      lang: '',
      recognizing: true,
    });

    const transferLang = _lang?.map((lang) => {
      return LANGUAGE_CODE_MAPPING[lang as keyof typeof LANGUAGE_CODE_MAPPING] || lang;
    });

    const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(_token, DEFAULT_SPEECH_CONFIG.region);
    speechConfig.setProperty('SpeechServiceResponse_PostProcessingOption', DEFAULT_SPEECH_CONFIG.postProcessingOption);
    var audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    // 发现日语偶尔会被识别成比如说韩语，日本同事反馈了，那么就只测试日语吧。
    const autoDetectSourceLanguageConfig = SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(
      _lang ? transferLang : autoDetectSourceLanguage
    );
    autoDetectSourceLanguageConfig.mode = SpeechSDK.LanguageIdMode[DEFAULT_SPEECH_CONFIG.languageIdMode];
    const recognizer = SpeechSDK.SpeechRecognizer.FromConfig(speechConfig, autoDetectSourceLanguageConfig, audioConfig);

    const phraseList = SpeechSDK.PhraseListGrammar.fromRecognizer(recognizer);
    SPEECH_PHRASE_LIST.forEach((word) => {
      phraseList.addPhrase(word);
    });

    recognizer.recognizing = (_s: any, e: any) => {
      console.log(`@@speech>>RECOGNIZING: Text=${e.result.text},  id=${e.result.resultId}`);

      const correctedText = correctSpeechText(e.result.text, e.result.language);

      const speechText = {
        text: correctedText,
        recognizing: true,
        lang: e.result.language,
        st: Date.now(),
      };
      setSpeechText(speechText);

      onRecognizing(speechText);
    };

    recognizer.recognized = (_s: any, e: any) => {
      console.log(`@@speech>>RECOGNIZED: Text=${e.result.text},  id=${e.result.resultId}`);
      const text = e.result.text;
      if (!text) return;
      if (typeof text === 'string' && !text.trim()) return;

      const correctedText = correctSpeechText(text, e.result.language);

      const speechText = {
        text: correctedText,
        recognizing: false,
        lang: e.result.language,
        st: Date.now(),
      };
      setSpeechText(speechText);
      onRecognized(speechText);
    };

    recognizer.canceled = (_s: any, e: any) => {
      console.log(`##speech CANCELED: Reason=${e.reason}`);
      stop();
      close();
      startInit(token);
    };

    recognizer.sessionStopped = (_s: any, e: any) => {
      console.log('##speech Session stopped.', e.sessionId);
      onStop && onStop(speechText);
    };

    recognizerRef.current = recognizer;
    return;
  };

  const startSpeak = () => {
    setSpeechText({
      st: Date.now(),
      text: '',
      lang: '',
      recognizing: false,
    });
    try {
      if (recognizerRef.current)
        recognizerRef.current.startContinuousRecognitionAsync(
          () => {
            console.log('Recognition started.');
            setStartState(true);
          },
          (err: any) => {
            console.error(`Failed to start recognition: ${err}`);
            setStartState(false);
          }
        );
    } catch (e) {
      console.log(e);
      setStartState(false);
    }
  };

  const stop = async () => {
    try {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(
          () => {
            setStartState(false);
          },
          (err: any) => {
            console.error(`Failed to stop recognition: ${err}`);
            setStartState(false);
          }
        );
      }
    } catch (e) {
      console.log(e);
      setStartState(false);
    }
  };

  const close = () => {
    clearInterval(intervalRef.current);
    if (recognizerRef.current) recognizerRef.current.close();
    recognizerRef.current = null;
  };

  return {
    startInit,
    stop,
    speechText,
    startState,
    startSpeak,
  };
};
